// B-150 — a chart the assistant showed under its reply, drawn the way Reportes draws its own: the
// `Grafica` card (CierresReportes), the axes, grid, tooltip, legend and palette of the Comité de
// seguimiento. The chart arrives whole in the reply's `displays` (`{ type: "chart", chart }`): the
// backend read every figure from its own counts and metrics, so this only draws — it adds nothing
// up, and a value the backend had none for (`null`) is a gap, never a zero.
import { useId } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BarChart3, ChartPie, TrendingUp } from "lucide-react";

import { moneda } from "../../../utils/cierrePanel";

// The Comité's palette: the platform's primary first, then its blues.
const PRIMARIO = "hsl(var(--primary))";
const SERIE = [PRIMARIO, "#3b5ba9", "#6f86c2", "#93a3c4", "#c7d0e2", "#e2e8f0"];
const EJE = { tickLine: false, axisLine: false, fontSize: 11, stroke: "var(--brinks-muted-foreground)" };
const LEYENDA = { iconType: "circle", iconSize: 8, wrapperStyle: { fontSize: 11 } };
const CURSOR = { fill: "var(--brinks-surface)" };

const numero = new Intl.NumberFormat("es-CO");
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const compacto = (n) => {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1e9) return `${(n / 1e9).toFixed(1).replace(".", ",")} MM`;
  if (v >= 1e8) return `${Math.round(n / 1e6)} M`;
  if (v >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (v >= 1e3) return `${Math.round(n / 1e3)} k`;
  return String(Math.round(n));
};

// "2026-10-03" -> "3 oct"; any other key as it came.
const etiquetaDeFecha = (texto) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(texto || ""));
  return m ? `${Number(m[3])} ${MESES[Number(m[2]) - 1]}` : texto;
};

const valorDe = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

function Burbuja({ active, payload, label, formato }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[10px] border bg-card px-3 py-2 text-[11.5px] shadow-brinks-card">
      <p className="mb-1 font-medium text-brinks-foreground">{label ?? payload[0]?.name}</p>
      {payload.map((p) => (
        <p key={`${p.dataKey}-${p.name}`} className="flex items-center gap-2 text-brinks-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.name}</span>
          <span className="ml-auto pl-3 font-medium tabular-nums text-brinks-foreground">{formato(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

const ICONO = { line: TrendingUp, area: TrendingUp, donut: ChartPie };

export function GraficaAsistente({ chart }) {
  const id = useId().replace(/:/g, "");
  if (!chart || !Array.isArray(chart.categories) || !Array.isArray(chart.series)) return null;

  const { kind, title, subtitle, unit, groupBy, categories, series, omitted, excluded } = chart;
  const formato = unit === "money" ? (v) => moneda(v) : (v) => (v === null || v === undefined ? "—" : numero.format(v));
  const porFecha = groupBy === "business_date";
  const claves = series.map((_, i) => `s${i}`);
  const datos = categories.map((c, ci) => ({
    nombre: porFecha ? etiquetaDeFecha(c.label) : c.label,
    ...Object.fromEntries(series.map((s, si) => [claves[si], valorDe(s.values?.[ci])])),
  }));
  const varias = series.length > 1;
  const Icono = ICONO[kind] || BarChart3;
  const tooltip = <Tooltip cursor={CURSOR} content={<Burbuja formato={formato} />} />;

  let cuerpo;
  if (kind === "donut") {
    const partes = datos.map((d, i) => ({ nombre: d.nombre, valor: d.s0, fill: SERIE[i % SERIE.length] }));
    cuerpo = (
      <div className="grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={partes} dataKey="valor" nameKey="nombre" innerRadius="62%" outerRadius="88%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                {partes.map((p) => <Cell key={p.nombre} fill={p.fill} />)}
              </Pie>
              <Tooltip content={<Burbuja formato={formato} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-1.5 text-[12px]">
          {partes.map((p) => (
            <li key={p.nombre} className="flex items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p.fill }} />
              <span className="min-w-0 flex-1 truncate text-brinks-muted-foreground">{p.nombre}</span>
              <span className="font-medium tabular-nums text-brinks-foreground">{formato(p.valor)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  } else if (kind === "line") {
    cuerpo = (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datos} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
            <XAxis dataKey="nombre" {...EJE} />
            <YAxis tickFormatter={compacto} {...EJE} width={44} />
            {tooltip}
            {varias ? <Legend {...LEYENDA} /> : null}
            {series.map((s, i) => (
              <Line key={claves[i]} type="monotone" dataKey={claves[i]} name={s.label} stroke={SERIE[i]} strokeWidth={i ? 1.5 : 2.5} dot={{ r: 3 }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  } else if (kind === "area") {
    cuerpo = (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={datos} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
            <defs>
              {series.map((_, i) => (
                <linearGradient key={claves[i]} id={`${id}-${claves[i]}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIE[i]} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={SERIE[i]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
            <XAxis dataKey="nombre" {...EJE} />
            <YAxis tickFormatter={compacto} {...EJE} width={44} />
            {tooltip}
            {varias ? <Legend {...LEYENDA} /> : null}
            {series.map((s, i) => (
              <Area key={claves[i]} type="monotone" dataKey={claves[i]} name={s.label} stroke={SERIE[i]} strokeWidth={2.2} fill={`url(#${id}-${claves[i]})`} activeDot={{ r: 5 }} isAnimationActive={false} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  } else if (kind === "bar_horizontal") {
    cuerpo = (
      <div style={{ height: Math.max(160, datos.length * (varias ? 34 : 26) + 24) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }} barGap={1}>
            <CartesianGrid stroke="var(--brinks-border)" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tickFormatter={compacto} {...EJE} />
            <YAxis type="category" dataKey="nombre" width={130} {...EJE} />
            {tooltip}
            {varias ? <Legend {...LEYENDA} /> : null}
            {series.map((s, i) => (
              <Bar key={claves[i]} dataKey={claves[i]} name={s.label} fill={SERIE[i]} radius={[0, 4, 4, 0]} maxBarSize={18} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  } else {
    // "bar" and "grouped_bar": columns, one series per colour.
    cuerpo = (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} margin={{ top: 10, right: 6, bottom: 0, left: 0 }} barGap={2}>
            <CartesianGrid stroke="var(--brinks-border)" vertical={false} />
            <XAxis dataKey="nombre" {...EJE} interval={0} tick={{ fontSize: datos.length > 6 ? 10 : 11 }} />
            <YAxis tickFormatter={compacto} {...EJE} width={44} />
            {tooltip}
            {varias ? <Legend {...LEYENDA} /> : null}
            {series.map((s, i) => (
              <Bar key={claves[i]} dataKey={claves[i]} name={s.label} fill={SERIE[i]} radius={[6, 6, 0, 0]} maxBarSize={46} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const notas = [
    omitted ? `Se muestran los ${categories.length} mayores; ${omitted} más no caben en la gráfica.` : null,
    ...(Array.isArray(excluded) ? excluded : []).map((e) => (
      `${e.series}: ${e.count} cierre${e.count === 1 ? "" : "s"} sin el dato no se cuentan.`)),
  ].filter(Boolean);

  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border bg-card shadow-brinks-card" aria-label={title}>
      <header className="flex items-start gap-3 border-b px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-brinks-surface text-brinks-foreground">
          <Icono className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold text-brinks-foreground">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">{subtitle}</p> : null}
        </div>
      </header>
      <div className="px-3 py-3">
        {categories.length ? cuerpo : (
          <p className="py-6 text-center text-[12.5px] text-brinks-muted-foreground">Sin datos para graficar en este periodo.</p>
        )}
        {notas.map((nota) => (
          <p key={nota} className="mt-2 px-1 text-[11px] text-brinks-muted-foreground">{nota}</p>
        ))}
      </div>
    </section>
  );
}
